import { useState, useEffect } from 'react';
// ============================================================
// store/axesStore.js
// EV Powertrain Simulator — 그래프 축 전역 상태 관리
// 패턴: 싱글톤(Singleton) — getAxesStore()로 전역 인스턴스 접근
// 사용법: import { useAxesStore } from '../../store/axesStore.js';
//
// 목적: 각 탭 컴포넌트의 로컬 useAxes() state를 전역 스토어로 끌어올려
//       대시보드 레이아웃 CSV 저장/불러오기를 가능하게 함.
//
// 사용법 (탭 컴포넌트에서):
//   import { useAxesStore } from '../../store/axesStore.js';
//   const ax = useAxesStore('RL_R1', { xMin:0, xMax:300, yMin:0, yMax:5000 });
//   // ax.axX, ax.setAxX, ax.domX, ax.hideY, ax.setHideY 등 useAxes와 동일 인터페이스
//
// ── 전역 축 ID 명명 규칙 ────────────────────────────────────
//   탭접두사_변수명   예: RL_R1, DC_1, DR_JERK_TS, AF_VDR1
// ============================================================

// ── 기본값 팩토리 ─────────────────────────────────────────────
/**
 * 단일 축 항목(AxisEntry) 객체를 기본값으로 생성한다.
 *
 * AXES_REGISTRY에 등록된 defaults 또는 useAxesStore 호출 시 넘긴 defaults로
 * 초기값을 구성하며, 명시되지 않은 항목은 공통 fallback(auto: true, range: 0~1800/200)을 사용한다.
 *
 * @param {{ xMin?, xMax?, xAuto?, yMin?, yMax?, yAuto?, y2Min?, y2Max?, y2Auto?, y3Min?, y3Max?, y3Auto? }} [defaults={}]
 * @returns {{ axX: object, axY: object, axY2: object, axY3: object, hideY: boolean, hideY2: boolean, hideY3: boolean }}
 */
function makeAxisEntry(defaults = {}) {
  const d = defaults || {};
  return {
    axX:   { auto: d.xAuto  !== false, min: d.xMin  != null ? d.xMin  : 0,   max: d.xMax  != null ? d.xMax  : 1800 },
    axY:   { auto: d.yAuto  !== false, min: d.yMin  != null ? d.yMin  : 0,   max: d.yMax  != null ? d.yMax  : 200  },
    axY2:  { auto: d.y2Auto !== false, min: d.y2Min != null ? d.y2Min : 0,   max: d.y2Max != null ? d.y2Max : 200  },
    axY3:  { auto: d.y3Auto !== false, min: d.y3Min != null ? d.y3Min : 0,   max: d.y3Max != null ? d.y3Max : 200  },
    hideY:  false,
    hideY2: false,
    hideY3: false,
  };
}

// ── 모든 축의 기본 설정 레지스트리 ───────────────────────────
// 키: 전역 축 ID, 값: useAxes() defaults와 동일한 { xMin, xMax, yMin, yMax, y2Min, y2Max, ... }
const AXES_REGISTRY = {
  // ── RLTabContent ──────────────────────────────────────────
  RL_R1: { xMin:0, xMax:300, yMin:0, yMax:5000, y2Min:0, y2Max:500  },
  RL_R2: { xMin:0, xMax:300, yMin:0, yMax:1000 },
  RL_R3: { xMin:0, xMax:300, yMin:0, yMax:50,   y2Min:0, y2Max:10   },
  RL_R4: { xMin:0, xMax:300, yMin:0, yMax:100  },
  RL_R5: { xMin:0, xMax:300, yMin:0, yMax:125,  y2Min:0, y2Max:1000 },
  // ── LaunchTabContent ─────────────────────────────────────
  LA_L1: { xMin:0, xMax:30, yMin:0, yMax:200,  y2Min:0, y2Max:20000, y3Min:-15, y3Max:30 },
  LA_L2: { xMin:0, xMax:30, yMin:0, yMax:500,  y2Min:0, y2Max:300,   y3Min:0,   y3Max:3  },
  LA_L3: { xMin:0, xMax:30, yMin:0, yMax:80   },
  // ── ReAccelTabContent ────────────────────────────────────
  RA_RA1: { xMin:0, xMax:15,  yMin:0, yMax:180, y2Min:0, y2Max:500, y3Min:0, y3Max:200 },
  RA_RA2: { xMin:0, xMax:200, yMin:0, yMax:400 },
  RA_RA3: { xMin:0, xMax:30,  yMin:0, yMax:180 },
  // ── CoastTabContent ──────────────────────────────────────
  CT_CT1: { xMin:0, xMax:60,  yMin:0, yMax:180,  y2Min:0, y2Max:8000 },
  CT_CT2: { xMin:0, xMax:180, yMin:0, yMax:200  },
  CT_CT3: { xMin:0, xMax:180, yMin:0, yMax:1000 },
  CT_CT4: { xMin:0, xMax:180, yMin:0, yMax:200  },
  // ── RegenBrakeTabContent ─────────────────────────────────
  RB_RB1: { xMin:0,   xMax:220, yMin:0, yMax:200  },
  RB_RB2: { xMin:0,   xMax:100, yMin:0, yMax:30   },
  RB_RB3: { xMin:-10, xMax:0,   yMin:0, yMax:110, y2Min:0, y2Max:300 },
  // ── EnergyTabContent ─────────────────────────────────────
  EN_E1:  { xMin:0, xMax:1800, yMin:0,  yMax:80  },
  EN_E2:  { xMin:0, xMax:1800, yMin:0,  yMax:100, y2Min:0, y2Max:100 },
  EN_E3:  { xMin:0, xMax:220,  yMin:0,  yMax:200 },
  EN_E4:  { xMin:0, xMax:300, xAuto:false, yMin:35, yMax:100, y2Min:0, y2Max:300 },
  // ── DCTabContent ─────────────────────────────────────────
  DC_1:  { xMin:0, xMax:1800, yMin:0,   yMax:180, y2Min:0,  y2Max:50  },
  DC_2:  { xMin:0, xMax:1800, yMin:0,   yMax:200, y2Min:0,  y2Max:100 },
  DC_3:  { xMin:0, xMax:40,   yMin:0,   yMax:40  },
  DC_4:  { xMin:0, xMax:1800, yMin:0,   yMax:80  },
  DC_5:  { xMin:0, xMax:10,   yMin:0,   yMax:30  },
  DC_6:  { xMin:0, xMax:40,   yMin:0,   yMax:100 },
  DC_7:  { xMin:0, xMax:3600, yMin:0,   yMax:160 },
  DC_8:  { xMin:0, xMax:3600, yMin:0,   yMax:200 },
  DC_9:  { xMin:0, xMax:3600, yMin:-10, yMax:10  },
  DC_10: { xMin:0, xMax:3600, yMin:-50, yMax:50  },
  // ── DrivabilityTabContent ────────────────────────────────
  DR_JERK_TS:  { xMin:0, xMax:10, yMin:-10, yMax:10  },
  DR_JERK_SPD: { xMin:0, xMax:10, yMin:0,   yMax:180, y2Min:-5,  y2Max:5  },
  DR_JERK_BRK: { xMin:0, xMax:10, yMin:0,   yMax:8000,y2Min:-15, y2Max:15 },
  DR_JERK_TF:  { xMin:0, xMax:10, yMin:0,   yMax:30  },
  // ── SuspensionRolloverTabContent ─────────────────────────
  SR_RV1: { xMin:0, xMax:10, yMin:-20,  yMax:20   },
  SR_RV2: { xMin:0, xMax:10, yMin:0,    yMax:12000 },
  SR_RV3: { xMin:0, xMax:10, yMin:-10,  yMax:10,   y2Min:-1.5, y2Max:1.5 },
  SR_RV4: { xMin:0, xMax:10, yMin:-1,   yMax:1,    y2Min:0,    y2Max:100 },
  SR_RV5: { xMin:0, xMax:10, yMin:-30,  yMax:30,   y2Min:-20,  y2Max:20  },
  // ── CompareTabContent ────────────────────────────────────
  CMP_E: { xMin:0, xMax:1800, yMin:60, yMax:100 },
  CMP_T: { xMin:0, xMax:1800, yMin:0,  yMax:80  },
  // ── AdvancedFinalTabContent ──────────────────────────────
  AF_VDR1: { xMin:0,   xMax:180,  yMin:0,   yMax:6000, y2Min:0,   y2Max:120 },
  AF_VDR2: { xMin:0,   xMax:180,  yMin:0,   yMax:30   },
  AF_VDR3: { xMin:0,   xMax:180,  yMin:-30, yMax:30   },
  AF_VDL1: { xMin:0,   xMax:30,   yMin:0,   yMax:160,  y2Min:0,   y2Max:250 },
  AF_VDC1: { xMin:0,   xMax:30,   yMin:0,   yMax:130,  y2Min:0,   y2Max:100 },
  AF_ENE2: { xMin:0,   xMax:30,   yMin:0,   yMax:50,   y2Min:0,   y2Max:100 },
  AF_ENE3: { xMin:0,   xMax:180,  yMin:0,   yMax:40,   y2Min:0.6, y2Max:1.0 },
  AF_THT1: { xMin:0,   xMax:30,   yMin:0,   yMax:160,  y2Min:0,   y2Max:140 },
  AF_THT2: { xMin:0,   xMax:1800, yMin:0,   yMax:120,  y2Min:0,   y2Max:10  },
  AF_THT3: { xMin:-10, xMax:40,   yMin:10,  yMax:30,   y2Min:0,   y2Max:600 },
  AF_BTB1: { xMin:0,   xMax:2000, yMin:70,  yMax:100,  y2Min:0,   y2Max:50  },
  AF_BTB2: { xMin:-30, xMax:60,   yMin:0,   yMax:50   },
  AF_VCV2: { xMin:0,   xMax:180,  yMin:0,   yMax:30   },
  // ── ThermalTabContent ────────────────────────────────────
  TH_T5:  {},
  TH_TH2: { yMin: -10,  yMax: 80  },
  TH_TH3: { xMin: 150,  xMax: 800, yMin: 20,  yMax: 160  },
  TH_TH4: { xMin: -30,  xMax: 50,  yMin: 0,   yMax: 0.08, y2Min: 0.8, y2Max: 3.5 },
  // ── EnergyMgmtTabContent (PEMS) ──────────────────────────────────
  EM1_elev:  { xMin:0, xMax:40, yMin:-50,  yMax:500  },
  EM2_soc:   { xMin:0, xMax:40, yMin:0,    yMax:100  },
  EM3_power: { xMin:0, xMax:40, yMin:-100, yMax:200  },
  EM4_radar: { xMin:0, xMax:40, yMin:0,    yMax:100  },
  EM5_eff:   { xMin:0, xMax:30, yMin:0,    yMax:100  },
  EM6_pred:  { xMin:0, xMax:40, yMin:0,    yMax:100  },
  EM7_regen: { xMin:0, xMax:40, yMin:-50,  yMax:500  },
  // ── 메인 파일 직접 선언 ──────────────────────────────────
  MAIN_RE1: { xMin:0, xMax:180, yMin:0, yMax:700 },
  MAIN_RE2: { xMin:0, xMax:35,  yMin:0, yMax:70, y2Min:0, y2Max:12 },
};

// ── 스토어 초기 상태 구성 ─────────────────────────────────────
/**
 * AXES_REGISTRY에 등록된 모든 축 ID를 순회하여 초기 상태 맵을 구성한다.
 *
 * 앱 기동 시 또는 resetAll() 호출 시 사용되며 각 축을 makeAxisEntry()로 초기화한다.
 *
 * @returns {{ [axId: string]: AxisEntry }}
 */
function buildInitial() {
  const m = {};
  Object.entries(AXES_REGISTRY).forEach(([id, defs]) => {
    m[id] = makeAxisEntry(defs);
  });
  return m;
}

// ── 스토어 팩토리 ────────────────────────────────────────────
/**
 * 그래프 축 상태 스토어 인스턴스를 생성한다.
 *
 * 영속화: localStorage 저장 없음. 축 설정은 세션 메모리에만 보관된다.
 *          대시보드 CSV 저장/불러오기는 bulkSet()을 통해 외부에서 직접 주입한다.
 *
 * @returns {object}  스토어 인스턴스
 */
function createAxesStore() {
  let state = {
    axes: buildInitial(),   // { [axId]: { axX, axY, axY2, axY3, hideY, hideY2, hideY3 } }

    /**
     * 특정 축 ID의 축 객체(axX, axY, axY2, axY3) 중 하나를 갱신한다.
     *
     * useAxesStore가 반환하는 setAxX/setAxY 등의 setter가 내부적으로 이 메서드를 호출한다.
     *
     * @param {string} id       전역 축 ID (예: 'RL_R1')
     * @param {string} axisKey  갱신할 축 키 ('axX' | 'axY' | 'axY2' | 'axY3')
     * @param {{ auto: boolean, min: number, max: number }} value  새 축 설정
     */
    setAxis(id, axisKey, value) {
      if (!state.axes[id]) return;
      state.axes = {
        ...state.axes,
        [id]: { ...state.axes[id], [axisKey]: value },
      };
      state._notify();
    },

    /**
     * 특정 축 ID의 boolean 가시성 플래그를 갱신한다.
     *
     * useAxesStore가 반환하는 setHideY/setHideY2/setHideY3 setter가 호출한다.
     * 함수형 업데이터는 useAxesStore 레벨에서 이미 평가된 값을 받는다.
     *
     * @param {string}  id       전역 축 ID
     * @param {string}  flagKey  갱신할 플래그 키 ('hideY' | 'hideY2' | 'hideY3')
     * @param {boolean} value    새 값
     */
    setFlag(id, flagKey, value) {
      if (!state.axes[id]) return;
      state.axes = {
        ...state.axes,
        [id]: { ...state.axes[id], [flagKey]: value },
      };
      state._notify();
    },

    /**
     * 여러 축 설정을 한 번에 부분 교체한다.
     *
     * 대시보드 레이아웃 CSV 불러오기 시 저장된 축 설정 전체를 복원하는 용도로 사용한다.
     * AXES_REGISTRY에 없는 axId는 무시한다.
     *
     * @param {{ [axId: string]: Partial<AxisEntry> }} axesMap  교체할 축 설정 맵
     */
    bulkSet(axesMap) {
      // axesMap: { [axId]: Partial<AxisEntry> }
      const next = { ...state.axes };
      Object.entries(axesMap).forEach(([id, partial]) => {
        if (next[id]) {
          next[id] = { ...next[id], ...partial };
        }
      });
      state.axes = next;
      state._notify();
    },

    /**
     * 모든 축 설정을 AXES_REGISTRY 기본값으로 초기화한다.
     *
     * 사용자가 축 설정을 리셋하거나 레이아웃을 초기 상태로 되돌릴 때 호출한다.
     */
    resetAll() {
      state.axes = buildInitial();
      state._notify();
    },

    // ── 내부
    _listeners: new Set(),
    /** 등록된 모든 구독자에게 현재 상태를 동기 전달한다. */
    _notify() { state._listeners.forEach(fn => fn(state)); },
    /**
     * 상태 변경 구독을 등록하고, 해제 함수를 반환한다.
     * @param {(state: object) => void} fn
     * @returns {() => void}
     */
    subscribe(fn) {
      state._listeners.add(fn);
      return () => state._listeners.delete(fn);
    },
    /**
     * 현재 스토어 상태 스냅샷을 반환한다.
     * @returns {object}
     */
    getState() { return state; },
  };
  return state;
}

// ── 싱글톤 인스턴스 (앱 전역 공유) ──────────────────────────
let _axesStoreInstance = null;

/**
 * 앱 전역 AxesStore 싱글톤 인스턴스를 반환한다.
 *
 * 최초 호출 시에만 createAxesStore()로 인스턴스를 생성하고 이후 호출에서는
 * 동일 인스턴스를 반환한다. 모든 탭이 동일한 축 상태를 공유하게 된다.
 *
 * @returns {object}  AxesStore 싱글톤 인스턴스
 */
function getAxesStore() {
  if (!_axesStoreInstance) _axesStoreInstance = createAxesStore();
  return _axesStoreInstance;
}

// ── React 훅: useAxesStore(id, defaults?) ────────────────────
// useAxes()와 완전히 동일한 반환 인터페이스를 유지
// → 기존 탭 코드에서 useAxes(defaults) → useAxesStore(id, defaults) 로 교체만 하면 됨

/**
 * 전역 AxesStore에서 특정 축 ID의 상태를 구독하는 React 훅.
 *
 * 기존 로컬 useAxes() 훅과 완전히 동일한 반환 인터페이스를 제공하므로
 * 탭 컴포넌트에서 useAxes(defaults) → useAxesStore(id, defaults) 로만 교체하면 된다.
 *
 * 동작 원리:
 *   1. getAxesStore()로 싱글톤 인스턴스를 가져온다.
 *   2. 첫 마운트 시 해당 axId가 AXES_REGISTRY에 없으면 makeAxisEntry(defaults)로 동적 등록한다.
 *   3. useState로 로컬 스냅샷을 유지하고 useEffect에서 store.subscribe()를 통해 갱신한다.
 *   4. auto 플래그에 따라 Recharts domain을 'auto'/'auto' 또는 [min, max]로 반환한다.
 *
 * @param {string} axId      전역 축 ID (예: 'RL_R1', 'DC_1')
 * @param {object} [defaults]  첫 등록 시 사용할 기본값 ({ xMin, xMax, yMin, yMax, ... })
 * @returns {{
 *   axX: object, setAxX: function, domX: Array,
 *   axY: object, setAxY: function, domY: Array,
 *   axY2: object, setAxY2: function, domY2: Array,
 *   axY3: object, setAxY3: function, domY3: Array,
 *   hideY: boolean, setHideY: function,
 *   hideY2: boolean, setHideY2: function,
 *   hideY3: boolean, setHideY3: function,
 * }}
 */
function useAxesStore(axId, defaults) {
  const store = getAxesStore();

  // 첫 마운트 시 해당 ID가 레지스트리에 없으면 동적으로 등록
  if (!store.getState().axes[axId]) {
    store.getState().axes[axId] = makeAxisEntry(defaults);
  }

  const [snap, setSnap] = useState(() => ({ ...store.getState().axes[axId] }));

  useEffect(() => {
    return store.subscribe((s) => {
      const entry = s.axes[axId];
      if (entry) setSnap({ ...entry });
    });
  }, [axId]);

  const { axX, axY, axY2, axY3, hideY, hideY2, hideY3 } = snap;

  // domain 계산 (useAxes와 동일)
  // [설계 의도] auto=true이면 Recharts가 데이터 범위를 자동 산출하고,
  // false이면 사용자가 직접 지정한 min/max를 고정 domain으로 사용한다.
  const domX  = axX.auto  ? ['auto','auto'] : [axX.min,  axX.max];
  const domY  = axY.auto  ? ['auto','auto'] : [axY.min,  axY.max];
  const domY2 = axY2.auto ? ['auto','auto'] : [axY2.min, axY2.max];
  const domY3 = axY3.auto ? ['auto','auto'] : [axY3.min, axY3.max];

  // setter — store.setAxis 래퍼
  const setAxX  = (v) => store.setAxis(axId, 'axX',  v);
  const setAxY  = (v) => store.setAxis(axId, 'axY',  v);
  const setAxY2 = (v) => store.setAxis(axId, 'axY2', v);
  const setAxY3 = (v) => store.setAxis(axId, 'axY3', v);
  // [설계 의도] setHideY에 함수형 업데이터(prev => !prev)를 허용하기 위해
  // store.setFlag 호출 전에 현재 snap 값을 기준으로 평가한다.
  const setHideY  = (v) => store.setFlag(axId, 'hideY',  typeof v === 'function' ? v(snap.hideY)  : v);
  const setHideY2 = (v) => store.setFlag(axId, 'hideY2', typeof v === 'function' ? v(snap.hideY2) : v);
  const setHideY3 = (v) => store.setFlag(axId, 'hideY3', typeof v === 'function' ? v(snap.hideY3) : v);

  return {
    axX, setAxX, axY, setAxY, domX, domY,
    axY2, setAxY2, domY2,
    axY3, setAxY3, domY3,
    hideY,  setHideY,
    hideY2, setHideY2,
    hideY3, setHideY3,
  };
}

export { createAxesStore, getAxesStore, useAxesStore, AXES_REGISTRY, makeAxisEntry };